import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/common/SEO'

const M = '#0d0d1a'
const G = '#d4a843'
const BR = 'rgba(255,255,255,0.08)'
const CA = 'rgba(255,255,255,0.04)'
const MU = 'rgba(255,255,255,0.45)'

const SITE_URL = 'https://still-waters-university.vercel.app'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${SITE_URL}/reset-password`,
    })
    if (error) { setError(error.message); setLoading(false) }
    else setDone(true)
  }

  if (done) return (
    <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Inter', sans-serif" }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontSize: 52, marginBottom: 20 }}>📬</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Check your email</h2>
        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          We sent a password reset link to <strong style={{ color: '#fff' }}>{email}</strong>. Click it within 1 hour to set a new password.
        </p>
        <motion.button whileHover={{ scale: 1.02 }} onClick={() => navigate('/signin')}
          style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#0d0d1a', border: 'none', borderRadius: 12, padding: '13px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Back to Sign In
        </motion.button>
      </motion.div>
    </div>
  )

  return (
    <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Inter', sans-serif" }}>
      <SEO title="Forgot Password" noindex />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>Forgot your password?</h1>
          <p style={{ color: MU, fontSize: 14 }}>Enter your email and we'll send a reset link.</p>
        </div>
        <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: 32 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                style={{ width: '100%', padding: '13px 16px', background: CA, border: `1.5px solid ${BR}`, borderRadius: 11, color: 'rgba(255,255,255,0.88)', fontSize: 15, outline: 'none', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' as const }}
                onFocus={e => (e.target.style.borderColor = G)} onBlur={e => (e.target.style.borderColor = BR)} />
            </div>
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#f87171' }}>
                  {error}
                </motion.div>
              )}
            </AnimatePresence>
            <motion.button type="submit" disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#0d0d1a', border: 'none', borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </motion.button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: MU }}>
            Remember it?{' '}
            <button onClick={() => navigate('/signin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: G, fontWeight: 600, fontSize: 13, padding: 0 }}>Sign in</button>
          </p>
        </div>
      </motion.div>
    </div>
  )
}